/* eslint-disable no-underscore-dangle */
/* eslint-disable class-methods-use-this */
import React from 'react';
import ReactDOM from 'react-dom';
import { createMemoryHistory } from 'history';
import AnalyticsSDK, { SUPPORTED_TARGETS, LOG_LEVELS } from '@adsk/bim360-analytics-sdk-js';
import lodashGet from 'lodash/get';
import ReduxProvider from './components/ReduxProvider';
import Redispatcher from '../services/redispatcher';
import ApiSdk from '../services/api.sdk';
import docsSDKWrapper from '../services/docsSDKWrapper';
import createRFIsStore from '../redux/store';
import { parseQueryString, stringifyQuery } from '../utils/query-helpers';
import { initialize as initializeFF } from '../utils/featureFlags';
import { ENV_CONFIGS, ISSUES_ENV_TYPES } from '../constants/envs';
import {
  configureJsonApiAction,
  configureWorkflowsApiAction,
  configureOxygenAction,
  configureDocumentAction,
  configureSheetsAction,
  setTargetUrnAction,
  changeLocationAction,
  setDocumentThumbnailUrlPromiseAction,
  fetchCurrentUserAction,
  setCurrentProject,
  configureAnalyticsAction,
  setCurrentViewAction,
} from '../redux/actions';
import { getCurrentUser } from '../redux/selectors';
import { setLanguage, setLocale } from '../utils/i18n';

import { staticMandatorySuperProperties } from '../utils/analytics-helpers';
import { issueLink } from '../utils/issues-helpers';
import { getCurrentToken } from '../services/getCurrentToken';

export default class {
  static get events() {
    return Redispatcher.events;
  }

  constructor() {
    // This is the only thing that needs to be defined, here, b/c
    // it enables events to be immediately registered, regardless of mount pending status
    this.redispatcher = new Redispatcher();
    this.getState = this.getState.bind(this);
    this.dispatch = this.dispatch.bind(this);
  }

  /*
   *
   * External API for the containing app to interact with issues-ui
   *
   */

  on(eventName, callback) {
    return this.redispatcher.on(eventName, callback);
  }

  setPath(path) {
    return Promise.resolve(this.store.dispatch(changeLocationAction(path)));
  }

  // used by docs
  isInEditOrNewProcess() {
    const { isInCreateRfiProcess } = this.store.getState().issues;
    return isInCreateRfiProcess;
  }

  dispatch(action) {
    return this.store.dispatch(action);
  }

  getState() {
    return this.store.getState();
  }

  setTargetUrn(targetUrn) {
    this.store.dispatch(setTargetUrnAction(targetUrn));
  }

  setSheetsConfig(sheetsConfig = {}) {
    const sheetsConfigObj = {
      sheetList: [],
      currentSheets: Array.isArray(sheetsConfig.currentSheet)
        ? sheetsConfig.currentSheet
        : [].concat(sheetsConfig.currentSheet),
    };

    // Simplify sheets objects in sheetList
    if (sheetsConfig && sheetsConfig.sheetList) {
      sheetsConfig.sheetList.forEach((sheet) => {
        sheetsConfigObj.sheetList.push({
          guid: sheet.guid,
          name: sheet.name,
        });
      });
    }

    this.store.dispatch(configureSheetsAction(sheetsConfigObj));
  }

  setDocumentThumbnailUrlPromise(thumbnailPromise) {
    this.store.dispatch(setDocumentThumbnailUrlPromiseAction(thumbnailPromise));
  }

  unmount(domElement) {
    // boolean unmountComponentAtNode(DOMElement container)
    //   Remove a mounted React component from the DOM and clean up its event handlers
    //   and state. If no component was mounted in the container, calling this function
    //   does nothing. Returns true if a component was unmounted and false if there was
    //   no component to unmount.
    return ReactDOM.unmountComponentAtNode(domElement);
  }

  configureExternalControls() {
    this.pushpinsControl = null;
  }

  _initializeApp(
    targetDomElement,
    token,
    {
      apiConfig,
      oxygenMappings,
      documentConfig,
      getIssueLink,
      analyticsConfig = {},
      currentProject = {},
      onError,
    } = {}
  ) {
    const configCopy = { ...apiConfig };
    const { env } = configCopy;
    const { region } = configCopy;
    // this used to change the app language for testing purposes.
    //   for example type in your console console `localStorage.setItem("issuesLocale", "de"), location.reload()` for german or "es" for spanish.
    //   to remove type `localStorage.removeItem("issuesLocale"), location.reload()`
    if (env !== ISSUES_ENV_TYPES.PROD && localStorage && localStorage.issuesLocale) {
      configCopy.language = localStorage.issuesLocale;
    }

    setLanguage(configCopy.language);
    setLocale(configCopy.language);
    const appHistory = createMemoryHistory({ queryKey: false, parseQueryString, stringifyQuery });
    this.configureExternalControls();
    const docsSDKConfig = {
      ...ENV_CONFIGS[region][env].DOCS_SDK_CONFIG,
      refreshToken: (callback) => {
        getCurrentToken(ENV_CONFIGS[region][env].DOCS)
          // eslint-disable-next-line no-shadow
          .then((token) => callback(false, token))
          .catch((err) => callback(err));
      },
    };
    // eslint-disable-next-line new-cap
    const { docsSDK } = docsSDKWrapper(docsSDKConfig, token);
    // Initialize the analyticsManager
    const analyticsManager = AnalyticsSDK.init({
      moduleName: ENV_CONFIGS[region][env].ANALYTICS.MODULE_NAME,
      targets: {
        [SUPPORTED_TARGETS.MIXPANEL]: { token: ENV_CONFIGS[region][env].ANALYTICS.MIXPANEL.TOKEN },
      },
      logLevel:
        ENV_CONFIGS[region][env].ANALYTICS.ENV.toLowerCase() !== 'prod'
          ? LOG_LEVELS.INFO
          : LOG_LEVELS.ERROR,
      mandatorySuperProps: staticMandatorySuperProperties(region, env, configCopy.language),
      disableAutoSessionStartEvent: true,
      disableAutoResetSuperProperties: true,
    });
    const { containerId } = apiConfig;
    // eslint-disable-next-line no-multi-assign
    const store = (this.store = createRFIsStore({
      redispatcher: this.redispatcher,
      jsonApi: new ApiSdk(this.dispatch, this.getState, { env, region, containerId }),
      appHistory,
      pushpinsControl: this.pushpinsControl,
      docsSDK,
      analyticsManager,
    }));

    if (this.pushpinsControl) {
      this.pushpinsControl.setDispatch(store.dispatch);
    }

    store.dispatch(setCurrentViewAction(this.viewType));
    store.dispatch(configureJsonApiAction(configCopy));
    store.dispatch(configureWorkflowsApiAction(configCopy));
    // this can be here, as long as configureJsonApiAction is synchronous:
    if (!oxygenMappings) {
      const usersPromise = docsSDK.getUsersInProject(apiConfig.projectId);
      const rolesPromise = docsSDK.getRolesInProject(apiConfig.projectId);
      const companiesPromise = docsSDK.getCompeniesInProject(apiConfig.projectId);

      // eslint-disable-next-line no-param-reassign
      oxygenMappings = {
        usersPromise,
        rolesPromise,
        companiesPromise,
      };
    }
    store.dispatch(configureOxygenAction(oxygenMappings));
    store.dispatch(setCurrentProject(currentProject));
    store.dispatch(configureAnalyticsAction(analyticsConfig));
    // eslint-disable-next-line no-unused-expressions
    documentConfig && store.dispatch(configureDocumentAction(documentConfig));

    issueLink.func = getIssueLink;
    // Set a default for the current view type
    this.setPath(this.defaultRoute);

    return new Promise((resolve) => {
      const props = {
        key: `${configCopy.containerId}_${new Date().getTime()}`,
        store,
        history: appHistory,
        viewType: this.viewType,
        locale: configCopy.language,
        onError,
      };
      ReactDOM.render(React.createElement(ReduxProvider, props), targetDomElement, () => {
        resolve();
      });
    });
  }

  mount(targetDomElement, options) {
    const apiConfigCopy = { ...options.apiConfig };
    const { env } = apiConfigCopy;
    const { region } = apiConfigCopy;
    const { projectId } = apiConfigCopy;
    // Support for both Docs and HQ project object
    const accountId =
      lodashGet(options, 'currentProject.account.id') ||
      lodashGet(options, 'currentProject.accountId');

    const launchDarklyUserObject = { custom: { region, accountId, projectId } };
    const analyticsId = lodashGet(options, 'analyticsConfig.analyticsId');
    if (analyticsId) {
      launchDarklyUserObject.key = analyticsId;
    }

    return new Promise((resolve) => {
      /* eslint-disable no-unused-vars */
      Promise.all([
        initializeFF(env, region, launchDarklyUserObject),
        getCurrentToken(ENV_CONFIGS[region][env].DOCS),
      ]).then(([ldResponse, token]) => {
        this._initializeApp(targetDomElement, token, options).then(() => {
          resolve();
        });
      });
    });
    /* eslint-enable no-unused-vars */
  }

  getCurrentUserData() {
    return new Promise((resolve, reject) => {
      const _resolveCurrentUser = () => {
        // returns true if current user is resolved / rejected.
        const currentUser = getCurrentUser(this.store.getState());
        if (!currentUser) {
          this.store.dispatch(fetchCurrentUserAction());
        } else if (currentUser.attributes) {
          resolve(currentUser.attributes);
          return true;
        } else if (currentUser.error) {
          reject(currentUser.error);
          return true;
        }
        return false;
      };

      if (!_resolveCurrentUser()) {
        const _currentUserListenerUnsubscribe = this.store.subscribe(() => {
          if (_resolveCurrentUser()) {
            _currentUserListenerUnsubscribe();
          }
        });
      }
    });
  }
}
